import { execa } from 'execa'
import path from 'path'
import { createRequire } from 'module'
import { access } from 'fs/promises'

export interface BpCliInfo {
  path: string
  version: string
}

/**
 * Find the bundled bp CLI that ships with ADK CLI
 */
async function findBundledBpPath(): Promise<string | null> {
  try {
    // The bundled bp CLI is at dist/bp-cli/bin.js
    // - In development: packages/adk/dist/commands/bp-cli.js -> packages/cli/dist/bp-cli/bin.js
    // - In production (installed): node_modules/@botpress/adk/dist/commands/bp-cli.js -> ../../@botpress/adk-cli/dist/bp-cli/bin.js

    // Try to resolve the CLI package to find the bundled bp CLI
    const require = createRequire(import.meta.url)
    try {
      const cliPackagePath = require.resolve('@botpress/adk-cli/package.json')
      const cliDir = path.dirname(cliPackagePath)
      const bundledBpPath = path.join(cliDir, 'dist', 'bp-cli', 'bin.js')

      // Check if the file exists
      await access(bundledBpPath)
      return bundledBpPath
    } catch {
      // Not found via require.resolve, ignore
    }

    return null
  } catch {
    return null
  }
}

/**
 * Find the bp CLI path - checks bundled, local, then global
 */
async function findBpPath(projectPath?: string): Promise<string | null> {
  const basePath = projectPath || process.cwd()

  // 1. First, try bundled bp CLI (ships with ADK CLI)
  const bundledPath = await findBundledBpPath()
  if (bundledPath) {
    return bundledPath
  }

  // 2. Try to find local @botpress/cli in the project
  try {
    // Create a require function from the project path
    const require = createRequire(path.join(basePath, 'package.json'))

    // Try to resolve @botpress/cli
    const cliPath = require.resolve('@botpress/cli/package.json')
    const cliDir = path.dirname(cliPath)

    // The bp binary is in the bin directory
    return path.join(cliDir, 'bin.js')
  } catch {
    // Module not found, try alternative resolution
    try {
      const require = createRequire(path.join(process.cwd(), 'package.json'))
      const cliPath = require.resolve('@botpress/cli/package.json')
      const cliDir = path.dirname(cliPath)
      return path.join(cliDir, 'bin.js')
    } catch {
      return null
    }
  }
}

/**
 * Get version from bp CLI path
 */
async function getVersionFromPath(bpPath: string): Promise<string> {
  try {
    // Execute the CLI to get version (more reliable than reading package.json)
    const { stdout } = await execa('node', [bpPath, '--version'])
    return parseVersion(stdout)
  } catch {
    // Fallback to a default version if we can't execute
    return 'unknown'
  }
}

/**
 * Parse version from CLI output
 */
function parseVersion(versionOutput: string): string {
  // Extract version from output like "4.14.0" or "v4.14.0"
  const match = versionOutput.match(/v?(\d+\.\d+\.\d+)/)
  return match?.[1] || '0.0.0'
}

/**
 * Get bp CLI info (path and version)
 */
export async function getBpCli(projectPath?: string): Promise<BpCliInfo> {
  // Try bundled/local bp first
  const localBpPath = await findBpPath(projectPath)
  if (localBpPath) {
    const version = await getVersionFromPath(localBpPath)
    return {
      path: localBpPath,
      version,
    }
  }

  // Try global bp command as fallback
  try {
    const { stdout } = await execa('bp', ['--version'])
    const version = parseVersion(stdout)
    return {
      path: 'bp',
      version,
    }
  } catch {
    throw new Error('Botpress CLI not found. This should not happen as @botpress/cli is bundled with the ADK CLI.')
  }
}
